import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId } from "../../utils/currentSalon";
import { listSalonUsers } from "../../api/salonUsers";
import { listFeedbackFollowups } from "../../api/marketingFeedback";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import { getSalonTimezone } from "../../utils/salonTimezone";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import InfoTooltip from "../../components/common/InfoTooltip";
import ModalCloseX from "../../components/common/ModalCloseX";
import "../MarketingSectionCompact.css";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "dismissed", label: "Dismissed" },
];

const PRIORITY_OPTIONS = [
  { value: "", label: "All priorities" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const CHANNEL_OPTIONS = [
  { value: "", label: "All channels" },
  { value: "portal", label: "Online booking" },
  { value: "in_salon", label: "In-salon" },
  { value: "unknown", label: "Unknown" },
];

const HELP_SECTIONS = [
  {
    heading: "How to use follow-ups",
    bullets: [
      "Filter by status, priority, staff, or date range to focus on what needs attention.",
      "Overdue follow-ups are highlighted when the SLA due time has passed.",
      "Open a follow-up to assign ownership, update resolution, add notes, or send a response.",
    ],
  },
];

const defaultFilters = {
  status: "",
  priority: "",
  assignee: "",
  staff_user_id: "",
  created_start: "",
  created_end: "",
  submitted_start: "",
  submitted_end: "",
  rating_min: "",
  rating_max: "",
  category_id: "",
  booking_channel: "",
  overdue_only: false,
};

const csvEscape = (value) => {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes("\"") || str.includes("\n")) {
    return `"${str.replace(/"/g, "\"\"")}"`;
  }
  return str;
};

const downloadCsv = (filename, headers, rows) => {
  const csv = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) => row.map(csvEscape).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getUserId = (user) => user?.user_id ?? user?.id ?? null;

const getUserName = (user) =>
  user?.full_name || user?.name || user?.username || user?.email || "";

export default function FeedbackFollowups() {
  const navigate = useNavigate();
  const { user, token, currentSalon } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);

  const [filters, setFilters] = useState(defaultFilters);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [salonUsers, setSalonUsers] = useState([]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await listSalonUsers(token);
      setSalonUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setSalonUsers([]);
    }
  }, [token]);

  const loadFollowups = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const params = {
        salon_id: salonId,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        assignee: filters.assignee || undefined,
        staff_user_id: filters.staff_user_id || undefined,
        created_start: filters.created_start || undefined,
        created_end: filters.created_end || undefined,
        submitted_start: filters.submitted_start || undefined,
        submitted_end: filters.submitted_end || undefined,
        rating_min: filters.rating_min || undefined,
        rating_max: filters.rating_max || undefined,
        category_id: filters.category_id || undefined,
        booking_channel: filters.booking_channel || undefined,
        overdue_only: filters.overdue_only ? true : undefined,
      };
      const data = await listFeedbackFollowups(params, token);
      setFollowups(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load follow-ups.");
      setFollowups([]);
    } finally {
      setLoading(false);
    }
  }, [filters, salonId, token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadFollowups();
  }, [loadFollowups]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters(defaultFilters);
  };

  const handleExport = () => {
    const headers = [
      "Ticket",
      "Client",
      "Rating",
      "Staff",
      "Category",
      "Assigned",
      "Status",
      "Priority",
      "Due (Salon Local)",
      "Created (Salon Local)",
      "Overdue",
    ];
    const rows = followups.map((row) => [
      row.ticket_number || row.ticket_id,
      row.client_name || "",
      row.overall_rating ?? "",
      row.technician_name || "",
      Array.isArray(row.category_labels) ? row.category_labels.join("; ") : "",
      row.assigned_to_name || "",
      row.status,
      row.priority,
      row.sla_due_at_utc
        ? formatDateTimeInTimeZone(row.sla_due_at_utc, salonTimezone)
        : "",
      row.created_at_utc
        ? formatDateTimeInTimeZone(row.created_at_utc, salonTimezone)
        : "",
      row.overdue ? "Yes" : "No",
    ]);
    downloadCsv("feedback-followups.csv", headers, rows);
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  const resolvedUsers = Array.isArray(salonUsers) ? salonUsers : [];

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Feedback Follow-ups
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6">
            <ReportHelpCard
              title="Follow-up Inbox"
              subtitle="Track, assign, and resolve client feedback follow-ups in one place."
              sections={HELP_SECTIONS}
              storageKey="sbin_feedback_followups_help"
            />

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Filters
                  </div>
                  <div className="text-sm text-slate-500">
                    Narrow the inbox to what needs action.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={loadFollowups}
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    Apply filters
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={handleExport}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(event) =>
                      handleFilterChange("status", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Priority
                  </label>
                  <select
                    value={filters.priority}
                    onChange={(event) =>
                      handleFilterChange("priority", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Assigned
                  </label>
                  <select
                    value={filters.assignee}
                    onChange={(event) =>
                      handleFilterChange("assignee", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">All assignees</option>
                    {resolvedUsers.map((item) => {
                      const userId = getUserId(item);
                      if (userId == null) return null;
                      return (
                        <option key={userId} value={userId}>
                          {getUserName(item)}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Staff
                  </label>
                  <select
                    value={filters.staff_user_id}
                    onChange={(event) =>
                      handleFilterChange("staff_user_id", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">All staff</option>
                    {resolvedUsers.map((item) => {
                      const userId = getUserId(item);
                      if (userId == null) return null;
                      return (
                        <option key={userId} value={userId}>
                          {getUserName(item)}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Created start
                  </label>
                  <input
                    type="date"
                    value={filters.created_start}
                    onChange={(event) =>
                      handleFilterChange("created_start", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Created end
                  </label>
                  <input
                    type="date"
                    value={filters.created_end}
                    onChange={(event) =>
                      handleFilterChange("created_end", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Submitted start
                  </label>
                  <input
                    type="date"
                    value={filters.submitted_start}
                    onChange={(event) =>
                      handleFilterChange("submitted_start", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Submitted end
                  </label>
                  <input
                    type="date"
                    value={filters.submitted_end}
                    onChange={(event) =>
                      handleFilterChange("submitted_end", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Rating min
                  </label>
                  <select
                    value={filters.rating_min}
                    onChange={(event) =>
                      handleFilterChange("rating_min", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">Any</option>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Rating max
                  </label>
                  <select
                    value={filters.rating_max}
                    onChange={(event) =>
                      handleFilterChange("rating_max", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">Any</option>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Category ID
                  </label>
                  <input
                    type="number"
                    value={filters.category_id}
                    onChange={(event) =>
                      handleFilterChange("category_id", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Booking channel
                  </label>
                  <select
                    value={filters.booking_channel}
                    onChange={(event) =>
                      handleFilterChange("booking_channel", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    {CHANNEL_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    checked={filters.overdue_only}
                    onChange={(event) =>
                      handleFilterChange("overdue_only", event.target.checked)
                    }
                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  <span className="text-sm text-slate-700">Overdue only</span>
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full table-fixed text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left w-40">
                      <span className="inline-flex items-center gap-2">
                        Created
                        <InfoTooltip text="Salon local time" label="Salon local time" />
                      </span>
                    </th>
                    <th className="px-3 py-2 text-left w-40">Client</th>
                    <th className="px-3 py-2 text-left w-24">Rating</th>
                    <th className="px-3 py-2 text-left w-40">Staff</th>
                    <th className="px-3 py-2 text-left w-48">Category</th>
                    <th className="px-3 py-2 text-left w-40">Assigned</th>
                    <th className="px-3 py-2 text-left w-32">Status</th>
                    <th className="px-3 py-2 text-left w-24">Priority</th>
                    <th className="px-3 py-2 text-left w-40">
                      <span className="inline-flex items-center gap-2">
                        Due
                        <InfoTooltip text="Salon local time" label="Salon local time" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading && (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan={9}>
                        Loading follow-ups...
                      </td>
                    </tr>
                  )}
                  {!loading && followups.length === 0 && (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan={9}>
                        No follow-ups found.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    followups.map((row) => (
                      <tr
                        key={row.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() =>
                          navigate(`/manager/marketing/feedback-followups/${row.id}`)
                        }
                      >
                        <td className="px-3 py-3 text-slate-700">
                          {row.created_at_utc
                            ? formatDateTimeInTimeZone(row.created_at_utc, salonTimezone)
                            : "--"}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div className="font-semibold text-slate-900">
                            {row.client_name || "Client"}
                          </div>
                          <div className="text-xs text-slate-500">
                            {row.ticket_number || `Ticket ${row.ticket_id}`}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {row.overall_rating ?? "--"}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {row.technician_name || "--"}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {Array.isArray(row.category_labels) &&
                          row.category_labels.length
                            ? row.category_labels.join(", ")
                            : "--"}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {row.assigned_to_name || "--"}
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              row.status === "resolved" || row.status === "dismissed"
                                ? "bg-emerald-50 text-emerald-700"
                                : row.status === "in_progress"
                                ? "bg-amber-50 text-amber-700"
                                : "bg-rose-50 text-rose-700"
                            }`}
                          >
                            {row.status.replace("_", " ")}
                          </span>
                          {row.overdue && (
                            <span className="ml-2 inline-flex rounded-full bg-rose-100 px-2 py-1 text-[10px] font-semibold uppercase text-rose-700">
                              Overdue
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-slate-700 capitalize">
                          {row.priority}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {row.sla_due_at_utc
                            ? formatDateTimeInTimeZone(row.sla_due_at_utc, salonTimezone)
                            : "--"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
